import { useState, type FormEvent } from 'react'
import type { Bot, PatchChannelBody, Posting, ReplyBotInfo } from '../../api/types'
import { Icon } from '../../components/Icon'
import { isRestricted, replyBotName, replyBotOf } from '../../lib/posting'
import { useBots } from '../../state/bots'
import { useConversation } from '../conversation/ConversationContext'

interface Props {
  /** Owners and admins; on a managed channel only sysadmins, whose saves are overrides. */
  canEdit: boolean
  onSave: (body: PatchChannelBody) => Promise<void>
}

/** Channel details: who can post, and which bot takes private replies (contract: "Restricted channels"). */
export function PostingSection({ canEdit, onSave }: Props) {
  const { channel } = useConversation()
  if (!canEdit) return isRestricted(channel) ? <PostingSummary /> : null
  // A change saved elsewhere starts the form again from the new settings.
  return <PostingForm key={`${channel.posting}:${channel.replyBot?.integrationId ?? ''}`} onSave={onSave} />
}

function PostingSummary() {
  const { channel, users } = useConversation()
  const bot = replyBotOf(channel)
  const replies = bot ? (bot.available ? `Replies go privately to ${replyBotName(bot, users)}.` : 'Replies are paused.') : 'Private replies are off.'
  return (
    <section className="drawer-section" aria-labelledby="posting-heading">
      <div className="section-heading">
        <h3 id="posting-heading">Posting</h3>
      </div>
      <p className="policy-notice" role="note">
        <Icon name="megaphone" />
        <span>Only owners and posters can post here. {replies}</span>
      </p>
    </section>
  )
}

interface ReplyBotOption {
  integrationId: string
  label: string
}

/** Bots that can take replies, plus the current reply bot when it can't right now (so the choice stays visible). */
function replyBotOptions(bots: Bot[] | null, current: ReplyBotInfo | null): ReplyBotOption[] {
  const options = (bots ?? []).filter((b) => b.acceptsMessages).map((b) => ({ integrationId: b.integrationId, label: `${b.name} (@${b.username})` }))
  if (current && !options.some((o) => o.integrationId === current.integrationId)) {
    options.unshift({ integrationId: current.integrationId, label: `${current.name} (@${current.username})${current.available ? '' : ', paused'}` })
  }
  return options
}

function PostingForm({ onSave }: { onSave: (body: PatchChannelBody) => Promise<void> }) {
  const { channel, users } = useConversation()
  const current = channel.replyBot ?? null
  const [posting, setPosting] = useState<Posting>(channel.posting ?? 'everyone')
  const [replyBotId, setReplyBotId] = useState(current?.integrationId ?? '')
  const [busy, setBusy] = useState(false)
  const restricted = posting === 'restricted'
  const bots = useBots(restricted)
  const options = replyBotOptions(bots.list, current)
  const dirty = posting !== (channel.posting ?? 'everyone') || (restricted && replyBotId !== (current?.integrationId ?? ''))

  const chosen = replyBotId ? (bots.list?.find((b) => b.integrationId === replyBotId) ?? null) : null
  const chosenIsCurrent = Boolean(current && current.integrationId === replyBotId)
  const chosenName = chosen?.name ?? (chosenIsCurrent && current ? replyBotName(current, users) : 'the bot')
  let help = "People who can't post can only read and react."
  if (replyBotId) {
    help =
      chosenIsCurrent && current && !current.available && !chosen?.acceptsMessages
        ? `Replies are paused: ${chosenName} can't receive messages right now.`
        : `People who can't post reply privately to ${chosenName}. It receives each reply with the message it answers.`
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!dirty) return
    setBusy(true)
    try {
      await onSave(restricted ? { posting, replyBotIntegrationId: replyBotId || null } : { posting })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="drawer-section" aria-labelledby="posting-heading">
      <div className="section-heading">
        <h3 id="posting-heading">Posting</h3>
        {isRestricted(channel) ? <span>Only owners and posters</span> : null}
      </div>
      <form onSubmit={(e) => void submit(e)}>
        <fieldset className="scope-fieldset">
          <legend className="field-label">Who can post</legend>
          <label className="check-row">
            <input id="posting-everyone" type="radio" name="posting" value="everyone" checked={!restricted} onChange={() => setPosting('everyone')} />
            <span>
              Everyone
              <small>Every member can post and reply.</small>
            </span>
          </label>
          <label className="check-row">
            <input id="posting-restricted" type="radio" name="posting" value="restricted" checked={restricted} onChange={() => setPosting('restricted')} />
            <span>
              Only owners and posters
              <small>Everyone else reads and reacts. Allow people to post in Members.</small>
            </span>
          </label>
        </fieldset>
        {restricted ? (
          <div className="field" style={{ marginTop: 12 }}>
            <label className="field-label" htmlFor="posting-replies">
              Replies
            </label>
            <select id="posting-replies" className="select-input" value={replyBotId} aria-describedby="posting-replies-help" onChange={(e) => setReplyBotId(e.target.value)}>
              <option value="">Off</option>
              {options.map((o) => (
                <option key={o.integrationId} value={o.integrationId}>
                  {o.label}
                </option>
              ))}
            </select>
            <p id="posting-replies-help" className="field-help">
              {help}
            </p>
            {bots.error && !bots.list ? (
              <p className="field-error" role="alert">
                {bots.error}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
          <button type="submit" className="button primary small" disabled={!dirty || busy}>
            Save posting
          </button>
        </div>
      </form>
    </section>
  )
}
